import { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSwappingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './DashboardView.scss';
import { AuthContext } from '../../contexts/AuthContext';
import PageTitle from '../PageTitle/PageTitle';
import AlertModal from '../AlertModal/AlertModal';
import { DynamicSelect } from '../Inputs/Inputs';
import StatCard from '../StatCard/StatCard';
import Card from '../Card/Card';
import Table from '../Table/Table';
import Heatmap from '../Heatmap/Heatmap';
import { Modal } from '../Modal/Modal';
import { loadDashboardData } from '../../services/dashboardMetrics';
import { loadCsatSummary } from '../../services/csatMetrics';
import { loadOffersSummary } from '../../services/offersMetrics';
import { loadHandoffSummary } from '../../services/handoffMetrics';
import { computeInsights } from '../../services/insights';
import { languageFlag, languageLabel } from '../../utils/languageFlag';
import { ReactComponent as Align } from '../../assets/align-center.svg';
import { ReactComponent as MessageIcon } from '../../assets/message-square.svg';
import { ReactComponent as CheckCircle } from '../../assets/check-circle.svg';
import { ReactComponent as AlertIcon } from '../../assets/alert-octagon.svg';
import { ReactComponent as Clock } from '../../assets/clock.svg';
import { ReactComponent as Users } from '../../assets/users.svg';
import { ReactComponent as Eye } from '../../assets/eye.svg';
import { ReactComponent as MousePointer } from '../../assets/mouse-pointer.svg';
import { ReactComponent as Layers } from '../../assets/layers.svg';
import { ReactComponent as Headphones } from '../../assets/headphones.svg';

// paleta categórica validada (skill dataviz, references/palette.md) — ordem fixa
const CATEGORICAL = [
  '#2a78d6',
  '#eb6834',
  '#1baf7a',
  '#eda100',
  '#e87ba4',
  '#008300',
];
const BRAND_BLUE = '#2198f3';

// Período único para todo o dashboard (secção 19 do plano Hotel Intelligence: "evitar que
// cada gráfico tenha uma lógica de filtros independente"). Antes, "Conversas por dia" e
// "Ofertas Especiais" tinham cada um o seu próprio seletor (7/30/90d vs Semana/Mês) e CSAT
// não recebia período nenhum.
const RANGE_OPTIONS = [
  { value: 'd7', label: '7 dias' },
  { value: 'd30', label: '30 dias' },
  { value: 'd90', label: '90 dias' },
];

const PERIOD_DAYS = { d7: 7, d30: 30, d90: 90 };

// início do período (N dias, incluindo hoje) — mesmo critério de dashboardMetrics.js.
function periodStartDate(rangeValue) {
  const days = PERIOD_DAYS[rangeValue] || 30;
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - (days - 1));
  return d;
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      {label && <div className="chart-tooltip-label">{label}</div>}
      {payload.map((p) => (
        <div className="chart-tooltip-row" key={p.dataKey || p.name}>
          <span className="chart-tooltip-value">
            {p.value}
            {p.unit || ''}
          </span>
          <span className="chart-tooltip-name">{p.name}</span>
        </div>
      ))}
    </div>
  );
}

function formatSeconds(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  return `${Math.round(seconds / 60)} min`;
}

function formatPercent(ratio) {
  if (ratio === null || ratio === undefined) return '—';
  return `${Math.round(ratio * 100)}%`;
}

// "Δ vs período anterior" (secção 3 do plano Hotel Intelligence). Devolve null
// (sem hint) quando o período anterior não tem dados suficientes para uma
// comparação com significado — evita "↑ ∞%" ou percentagens absurdas a partir
// de zero, coisa que o plano pede explicitamente para não fazer.
function formatDeltaPercent(current, previous) {
  if (current === null || current === undefined) return null;
  if (previous === null || previous === undefined || previous === 0) return null;
  const pct = Math.round(((current - previous) / previous) * 1000) / 10;
  if (pct === 0) return '→ igual ao período anterior';
  const arrow = pct > 0 ? '↑' : '↓';
  return `${arrow} ${Math.abs(pct)}% vs período anterior`;
}

// Igual, mas em pontos percentuais (para taxas já expressas em %, ex.
// Resolução automática — exemplo do plano: "↑ 3 p.p.").
function formatDeltaPP(current, previous) {
  if (current === null || current === undefined) return null;
  if (previous === null || previous === undefined) return null;
  const pp = Math.round((current - previous) * 1000) / 10;
  if (pp === 0) return '→ igual ao período anterior';
  const arrow = pp > 0 ? '↑' : '↓';
  return `${arrow} ${Math.abs(pp)} p.p. vs período anterior`;
}

function formatPeakHour(hour) {
  if (hour === null || hour === undefined) return '—';
  const next = (hour + 1) % 24;
  return `${String(hour).padStart(2, '0')}h–${String(next).padStart(2, '0')}h`;
}

// Cards de gráficos/tabelas reorganizáveis por arrastar (secção pedida: "sermos nós a
// decidir o que queremos ver primeiro"). Ordem guardada por browser (localStorage) — não
// sincroniza entre dispositivos, decisão deliberada para não precisar de endpoint novo no
// backend para uma preferência puramente de visualização.
const CARD_IDS = [
  'insights',
  'conversas-dia',
  'resolucao',
  'idiomas',
  'intencoes',
  'comparacao-hoteis',
  'csat',
  'ofertas',
  'horario',
  'handoff',
];
const DASHBOARD_ORDER_STORAGE_KEY = 'hd-dashboard-card-order-v1';

function loadCardOrder() {
  try {
    const raw = localStorage.getItem(DASHBOARD_ORDER_STORAGE_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(saved)) return CARD_IDS;
    const known = saved.filter((id) => CARD_IDS.includes(id));
    const missing = CARD_IDS.filter((id) => !known.includes(id));
    return [...known, ...missing];
  } catch {
    return CARD_IDS;
  }
}

function saveCardOrder(order) {
  try {
    localStorage.setItem(DASHBOARD_ORDER_STORAGE_KEY, JSON.stringify(order));
  } catch {
    // localStorage indisponível (modo privado, quota) — a ordem só não persiste
  }
}

function GripIcon() {
  return (
    <svg width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true">
      {[2, 8].map((cx) =>
        [2, 8, 14].map((cy) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1.5" fill="currentColor" />
        )),
      )}
    </svg>
  );
}

// Wrapper de cada card no grid arrastável — o `useSortable` mede/anima o próprio wrapper
// (por isso o span-2 fica aqui, não no <Card> lá dentro, senão o grid não via a largura
// certa), e a pega de arrastar é só o ícone, para não interferir com cliques em gráficos/
// linhas de tabela dentro do card.
function SortableCard({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} className="sortable-card">
      <button
        type="button"
        className="drag-handle"
        title="Arrastar para reorganizar"
        {...attributes}
        {...listeners}
      >
        <GripIcon />
      </button>
      {children}
    </div>
  );
}

// Vista partilhada do Dashboard, usada pela página do superadmin (/dashboard)
// e pela página dos clientes (/my-dashboard). O scoping por grupo vem de
// `accessibleGroups` (backend), pelo que cada utilizador só vê os seus grupos.
// `unansweredTo` aponta o card "Pedidos sem resposta" e `sessionDetailsTo` o
// drill-down de "Principais intenções" para a rota certa de cada contexto
// (/unanswered + /sessions no admin, /my-unanswered-messages + /my-sessions no cliente).
export default function DashboardView({ unansweredTo = '/unanswered', sessionDetailsTo = '/sessions' }) {
  const { accessibleGroups, axiosAuth } = useContext(AuthContext);

  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [hotels, setHotels] = useState([]);
  const [selectedHotelId, setSelectedHotelId] = useState('');
  const [range, setRange] = useState('d30');
  const [data, setData] = useState(null);
  const [intentModal, setIntentModal] = useState(null);
  const [csat, setCsat] = useState(null);
  const [offers, setOffers] = useState(null);
  const [handoff, setHandoff] = useState(null);
  const [cardOrder, setCardOrder] = useState(loadCardOrder);
  const [loading, setLoading] = useState(false);

  const dragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleCardDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setCardOrder((prev) => {
      // troca direta de posição (não "inserir e empurrar" — com cards largos/estreitos
      // misturados na mesma grelha, deslocar tudo o resto criava buracos/desalinhamentos).
      const next = [...prev];
      const i = next.indexOf(active.id);
      const j = next.indexOf(over.id);
      [next[i], next[j]] = [next[j], next[i]];
      saveCardOrder(next);
      return next;
    });
  }, []);
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: '',
    message: '',
  });

  const openError = useCallback((title, message) => {
    setErrorModal({ open: true, title, message });
  }, []);
  const closeError = useCallback(() => {
    setErrorModal({ open: false, title: '', message: '' });
  }, []);

  const groupOptions = useMemo(
    () =>
      (accessibleGroups || []).map((g) => ({
        value: String(g.id),
        label: g.name,
      })),
    [accessibleGroups],
  );

  const hotelOptions = useMemo(
    () => [
      { value: '', label: 'Todos os hotéis' },
      ...hotels.map((h) => ({ value: String(h.id), label: h.name })),
    ],
    [hotels],
  );

  useEffect(() => {
    if (accessibleGroups?.length && !selectedGroupId) {
      setSelectedGroupId(String(accessibleGroups[0].id));
    }
  }, [accessibleGroups, selectedGroupId]);

  // Lista de hotéis do grupo para o filtro de Hotel — reinicia para "Todos os hotéis"
  // sempre que o grupo muda, para nunca ficar com um hotel de outro grupo selecionado.
  useEffect(() => {
    setSelectedHotelId('');
    if (!selectedGroupId) {
      setHotels([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: result } = await axiosAuth.get(`/hotels/group/${selectedGroupId}`);
        if (!cancelled) setHotels(Array.isArray(result) ? result : []);
      } catch {
        if (!cancelled) setHotels([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedGroupId, axiosAuth]);

  useEffect(() => {
    if (!selectedGroupId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await loadDashboardData(
          axiosAuth,
          selectedGroupId,
          selectedHotelId,
          PERIOD_DAYS[range],
        );
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          openError(
            'Erro ao carregar o Dashboard',
            err?.response?.data?.detail || err.message,
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedGroupId, selectedHotelId, range, axiosAuth, openError]);

  useEffect(() => {
    if (!selectedGroupId) return;
    let cancelled = false;
    (async () => {
      try {
        const dateFrom = periodStartDate(range).toISOString();
        const result = await loadCsatSummary(
          axiosAuth,
          selectedGroupId,
          dateFrom,
          undefined,
          selectedHotelId,
        );
        if (!cancelled) setCsat(result);
      } catch {
        // módulo opcional/licenciado por hotel — nunca deve bloquear o resto do dashboard
        if (!cancelled) setCsat({ available: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedGroupId, selectedHotelId, range, axiosAuth]);

  useEffect(() => {
    if (!selectedGroupId) return;
    let cancelled = false;
    (async () => {
      try {
        const dateFrom = periodStartDate(range).toISOString();
        const result = await loadOffersSummary(
          axiosAuth,
          selectedGroupId,
          dateFrom,
          undefined,
          selectedHotelId,
        );
        if (!cancelled) setOffers(result);
      } catch {
        // nunca deve bloquear o resto do dashboard
        if (!cancelled) setOffers({ available: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedGroupId, selectedHotelId, range, axiosAuth]);

  useEffect(() => {
    if (!selectedGroupId) return;
    let cancelled = false;
    (async () => {
      try {
        const since = periodStartDate(range).toISOString();
        const result = await loadHandoffSummary(
          axiosAuth,
          selectedGroupId,
          since,
          undefined,
          selectedHotelId,
        );
        if (!cancelled) setHandoff(result);
      } catch {
        // módulo opcional/licenciado por hotel — nunca deve bloquear o resto do dashboard
        if (!cancelled) setHandoff({ available: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedGroupId, selectedHotelId, range, axiosAuth]);

  const conversationSeries = data?.conversationsByDay?.[range] || [];
  const languages = data?.languages || [];
  const intentions = data?.intentions || [];
  const hotelRanking = data?.hotelRanking || [];

  const insights = useMemo(
    () => computeInsights({ data, csat, offers, handoff, periodDays: PERIOD_DAYS[range] }),
    [data, csat, offers, handoff, range],
  );

  const previousIntentionsByKey = useMemo(
    () => new Map((data?.previousIntentions || []).map((t) => [t.key, t.count])),
    [data],
  );

  // Junta a série diária de resolução automática/sem resposta (dashboardMetrics.js) com a
  // % de conversas encaminhadas para operador nesse dia (handoffMetrics.js `daily` ÷
  // conversas desse dia em `conversationsByDay`) — secção 7 do plano Hotel Intelligence.
  // Só adiciona a 3ª série quando o módulo de Handoff está licenciado/disponível.
  const resolutionSeries = useMemo(() => {
    const base = data?.resolutionByDay || [];
    if (!handoff?.available) return base;
    const handoffByDate = new Map((handoff.daily || []).map((d) => [d.date, d.count]));
    const conversasByDate = new Map(
      (data?.conversationsByDay?.[range] || []).map((d) => [d.date, d.count]),
    );
    return base.map((row) => {
      const conversas = conversasByDate.get(row.date) || 0;
      const handoffCount = handoffByDate.get(row.date) || 0;
      return {
        ...row,
        encaminhamentoPct: conversas
          ? Math.round((handoffCount / conversas) * 1000) / 10
          : null,
      };
    });
  }, [data, handoff, range]);

  const hotelColumns = [
    { key: 'hotel_name', label: 'Hotel' },
    { key: 'conversas', label: 'Conversas' },
    {
      key: 'taxa_fallback',
      label: 'Taxa sem resposta',
      render: (row) => formatPercent(row.taxa_fallback),
    },
  ];

  const offerColumns = [
    { key: 'offer_title', label: 'Oferta', render: (row) => row.offer_title || row.offer_id },
    { key: 'views', label: 'Visualizações' },
    { key: 'clicks', label: 'Cliques' },
    {
      key: 'ctr_interaction',
      label: 'CTR Interação',
      render: (row) => formatPercent(row.ctr_interaction != null ? row.ctr_interaction / 100 : null),
    },
    { key: 'cta_clicks', label: 'Cliques CTA' },
    {
      key: 'ctr_conversion',
      label: 'CTR Conversão',
      render: (row) => formatPercent(row.ctr_conversion != null ? row.ctr_conversion / 100 : null),
    },
  ];

  const handoffReasonColumns = [
    { key: 'label', label: 'Motivo de encerramento' },
    { key: 'count', label: 'Conversas' },
  ];

  const handoffHotelColumns = [
    { key: 'hotel_name', label: 'Hotel' },
    { key: 'total', label: 'Encaminhamentos' },
  ];

  const pendingContactColumns = [
    { key: 'name', label: 'Nome' },
    { key: 'phone', label: 'Telefone', render: (row) => row.phone || '—' },
    { key: 'email', label: 'Email', render: (row) => row.email || '—' },
    {
      key: 'requested_at',
      label: 'Pedido em',
      render: (row) => (row.requested_at ? new Date(row.requested_at).toLocaleString('pt-PT') : '—'),
    },
  ];

  const intentionColumns = [
    { key: 'label', label: 'Intenção' },
    { key: 'count', label: 'Conversas' },
    { key: 'percentage', label: '%', render: (row) => `${row.percentage}%` },
    {
      key: 'evolution',
      label: 'Evolução',
      render: (row) =>
        formatDeltaPercent(row.count, previousIntentionsByKey.get(row.key)) || '—',
    },
    { key: 'action', label: '', render: () => 'Ver conversas →' },
  ];

  return (
    <div className="dashboard">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title}
          message={errorModal.message}
          onClose={closeError}
        />
      )}
      <Modal
        isVisible={!!intentModal}
        onClose={() => setIntentModal(null)}
        title={intentModal ? `Conversas — ${intentModal.label}` : ''}
        disableActions
      >
        {intentModal?.sample.length === 0 ? (
          <p className="chart-note">Sem conversas de exemplo disponíveis.</p>
        ) : (
          <ul className="intent-sample-list">
            {intentModal?.sample.map((m, i) => (
              <li key={i}>
                <p>{m.content}</p>
                <div className="intent-sample-meta">
                  <span>
                    {m.timestamp ? m.timestamp.toLocaleString('pt-PT') : '—'}
                  </span>
                  {m.session_id && (
                    <Link to={`${sessionDetailsTo}/${m.session_id}`}>Ver conversa</Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Modal>
      <PageTitle title="Dashboard" />

      {selectedGroupId && (
        <div className="filters dashboard-filters">
          {groupOptions.length > 1 && (
            <DynamicSelect
              label="Cliente"
              placeholder="Escolher um grupo"
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              options={groupOptions}
            />
          )}
          {hotels.length > 1 && (
            <DynamicSelect
              label="Hotel"
              value={selectedHotelId}
              onChange={(e) => setSelectedHotelId(e.target.value)}
              options={hotelOptions}
            />
          )}
          <div className="filter-period">
            <span className="filter-period-label">Período</span>
            <div className="range-toggle">
              {RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={range === opt.value ? 'active' : ''}
                  onClick={() => setRange(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div
        className={`container dashboard-content ${loading ? 'is-loading' : ''}`}
      >
        {!selectedGroupId && (
          <div className="empty">Escolhe um grupo para ver o Dashboard.</div>
        )}

        {selectedGroupId && (
          <>
            <div className="stat-grid">
              <StatCard
                label="Conversas hoje"
                value={data?.kpis.conversasHoje ?? '—'}
                icon={<Align />}
                loading={loading}
              />
              <StatCard
                label="Conversas no período"
                value={data?.kpis.conversasPeriodo ?? '—'}
                hint={formatDeltaPercent(
                  data?.kpis.conversasPeriodo,
                  data?.kpis.conversasPeriodoAnterior,
                )}
                icon={<MessageIcon />}
                loading={loading}
              />
              <StatCard
                label="Resolução automática"
                value={formatPercent(data?.kpis.taxaResolucaoAutomatica)}
                hint={formatDeltaPP(
                  data?.kpis.taxaResolucaoAutomatica,
                  data?.kpis.taxaResolucaoAutomaticaAnterior,
                )}
                icon={<CheckCircle />}
                loading={loading}
              />
              <StatCard
                label="Pedidos sem resposta"
                value={data?.kpis.perguntasSemResposta ?? '—'}
                icon={<AlertIcon />}
                variant={
                  data?.kpis.perguntasSemResposta ? 'warning' : 'default'
                }
                to={unansweredTo}
                hint="Ver lista"
                loading={loading}
              />
              <StatCard
                label="Tempo médio de resposta"
                value={formatSeconds(data?.kpis.tempoMedioRespostaSeg)}
                hint={formatDeltaPercent(
                  data?.kpis.tempoMedioRespostaSeg,
                  data?.kpis.tempoMedioRespostaAnteriorSeg,
                )}
                icon={<Clock />}
                loading={loading}
              />
              <StatCard
                label="Reservas iniciadas no período"
                value={data?.kpis.reservasPeriodo ?? '—'}
                hint={formatDeltaPercent(
                  data?.kpis.reservasPeriodo,
                  data?.kpis.reservasPeriodoAnterior,
                )}
                icon={<Layers />}
                loading={loading}
              />
              <StatCard
                label="Reservas totais"
                value={data?.kpis.reservasTotais ?? '—'}
                icon={<Layers />}
                loading={loading}
              />
              <StatCard
                label="Idioma mais comum"
                value={
                  data?.kpis.idiomaMaisComum
                    ? `${languageFlag(data.kpis.idiomaMaisComum)} ${languageLabel(data.kpis.idiomaMaisComum)}`
                    : '—'
                }
                loading={loading}
              />
            </div>

            <div className="chart-grid-toolbar">
              <span className="chart-grid-hint">
                Arrasta um card pelo ícone no canto para cima de outro para trocarem de lugar.
              </span>
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setCardOrder(CARD_IDS);
                  saveCardOrder(CARD_IDS);
                }}
              >
                Repor disposição
              </button>
            </div>

            {(() => {
              const cardRenderers = {
                insights: () =>
                  insights.length > 0 && (
                    <Card title="Insights IA">
                      <ul className="insights-list">
                        {insights.map((insight) => (
                          <li key={insight.id} className={`insight insight-${insight.type}`}>
                            {insight.text}
                          </li>
                        ))}
                      </ul>
                    </Card>
                  ),
                'conversas-dia': () => (
                  <Card title="Conversas por dia">
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart
                        data={conversationSeries}
                        margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                      >
                        <CartesianGrid
                          stroke="var(--color-border)"
                          strokeDasharray="0"
                          vertical={false}
                        />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 11, fill: 'var(--color-text-subtle)' }}
                          axisLine={{ stroke: 'var(--color-border)' }}
                          tickLine={false}
                        />
                        <YAxis
                          allowDecimals={false}
                          tick={{ fontSize: 11, fill: 'var(--color-text-subtle)' }}
                          axisLine={false}
                          tickLine={false}
                          width={28}
                        />
                        <Tooltip content={<ChartTooltip />} />
                        <Legend
                          wrapperStyle={{
                            fontSize: 12,
                            color: 'var(--color-text-muted)',
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="count"
                          name="Conversas"
                          stroke={BRAND_BLUE}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card>
                ),
                resolucao: () => (
                  <Card title="Evolução da resolução automática">
                    <p className="chart-note">
                      Baseado na mesma amostra de mensagens dos KPIs acima — grupos muito ativos
                      podem não ter histórico suficiente para preencher os {PERIOD_DAYS[range]} dias.
                    </p>
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart
                        data={resolutionSeries}
                        margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                      >
                        <CartesianGrid
                          stroke="var(--color-border)"
                          strokeDasharray="0"
                          vertical={false}
                        />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 11, fill: 'var(--color-text-subtle)' }}
                          axisLine={{ stroke: 'var(--color-border)' }}
                          tickLine={false}
                        />
                        <YAxis
                          domain={[0, 100]}
                          tickFormatter={(v) => `${v}%`}
                          tick={{ fontSize: 11, fill: 'var(--color-text-subtle)' }}
                          axisLine={false}
                          tickLine={false}
                          width={36}
                        />
                        <Tooltip content={<ChartTooltip />} />
                        <Legend
                          wrapperStyle={{
                            fontSize: 12,
                            color: 'var(--color-text-muted)',
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="resolucaoPct"
                          name="Resolução automática"
                          unit="%"
                          stroke={CATEGORICAL[2]}
                          strokeWidth={2}
                          dot={false}
                          connectNulls
                        />
                        <Line
                          type="monotone"
                          dataKey="semRespostaPct"
                          name="Sem resposta"
                          unit="%"
                          stroke={CATEGORICAL[1]}
                          strokeWidth={2}
                          dot={false}
                          connectNulls
                        />
                        {handoff?.available && (
                          <Line
                            type="monotone"
                            dataKey="encaminhamentoPct"
                            name="Encaminhamento p/ operador"
                            unit="%"
                            stroke={BRAND_BLUE}
                            strokeWidth={2}
                            dot={false}
                            connectNulls
                          />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </Card>
                ),
                idiomas: () => (
                  <Card title="Idiomas">
                    {languages.length === 0 ? (
                      <div className="empty">Sem dados suficientes.</div>
                    ) : (
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie
                            data={languages}
                            dataKey="count"
                            nameKey="lang"
                            innerRadius={55}
                            outerRadius={80}
                            paddingAngle={2}
                          >
                            {languages.map((entry, idx) => (
                              <Cell
                                key={entry.lang}
                                fill={CATEGORICAL[idx % CATEGORICAL.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip content={<ChartTooltip />} />
                          <Legend
                            formatter={(value) => languageLabel(value)}
                            wrapperStyle={{
                              fontSize: 12,
                              color: 'var(--color-text-muted)',
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </Card>
                ),
                intencoes: () => (
                  <Card title="Principais intenções dos hóspedes">
                    <p className="chart-note">
                      Aproximação por palavras-chave nas mensagens dos hóspedes —
                      não é uma classificação exata. Clica numa linha da tabela para ver conversas.
                    </p>
                    {intentions.length === 0 ? (
                      <div className="empty">Sem dados suficientes.</div>
                    ) : (
                      <>
                        <ResponsiveContainer width="100%" height={240}>
                          <BarChart
                            data={intentions}
                            layout="vertical"
                            margin={{ left: 8, right: 24 }}
                          >
                            <CartesianGrid
                              stroke="var(--color-border)"
                              strokeDasharray="0"
                              horizontal={false}
                            />
                            <XAxis
                              type="number"
                              allowDecimals={false}
                              tick={{
                                fontSize: 11,
                                fill: 'var(--color-text-subtle)',
                              }}
                              axisLine={false}
                              tickLine={false}
                            />
                            <YAxis
                              type="category"
                              dataKey="label"
                              width={140}
                              tick={{
                                fontSize: 12,
                                fill: 'var(--color-text-muted)',
                              }}
                              axisLine={false}
                              tickLine={false}
                            />
                            <Tooltip content={<ChartTooltip />} />
                            <Legend
                              wrapperStyle={{
                                fontSize: 12,
                                color: 'var(--color-text-muted)',
                              }}
                            />
                            <Bar
                              dataKey="count"
                              name="Conversas"
                              fill={BRAND_BLUE}
                              radius={[0, 4, 4, 0]}
                              maxBarSize={20}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                        <Table
                          columns={intentionColumns}
                          data={intentions}
                          onRowClick={(row) => setIntentModal(row)}
                        />
                      </>
                    )}
                  </Card>
                ),
                'comparacao-hoteis': () => (
                  <Card title="Comparação entre hotéis">
                    {hotelRanking.length === 0 ? (
                      <div className="empty">Sem dados suficientes.</div>
                    ) : (
                      <Table columns={hotelColumns} data={hotelRanking} />
                    )}
                  </Card>
                ),
                csat: () =>
                  csat?.available && (
                    <Card title="Satisfação">
                      <div className="csat-summary">
                        <div className="csat-average">
                          <span className="csat-average-value">
                            {csat.average != null ? csat.average.toFixed(1).replace('.', ',') : '—'} / 5
                          </span>
                          <span className="csat-stars">
                            {'⭐'.repeat(csat.average != null ? Math.round(csat.average) : 0)}
                          </span>
                          <span className="csat-total">{csat.total} avaliações</span>
                        </div>
                        <div className="csat-distribution">
                          {[5, 4, 3, 2, 1].map((n) => (
                            <div className="csat-dist-row" key={n}>
                              <span className="csat-dist-label">{n} ⭐</span>
                              <div className="csat-dist-bar">
                                <div
                                  className="csat-dist-fill"
                                  style={{ width: `${csat.percentages?.[String(n)] || 0}%` }}
                                />
                              </div>
                              <span className="csat-dist-pct">
                                {csat.percentages?.[String(n)] || 0}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {csat.time_series.length > 0 && (
                        <ResponsiveContainer width="100%" height={180}>
                          <LineChart
                            data={csat.time_series}
                            margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                          >
                            <CartesianGrid
                              stroke="var(--color-border)"
                              strokeDasharray="0"
                              vertical={false}
                            />
                            <XAxis
                              dataKey="date"
                              tick={{ fontSize: 11, fill: 'var(--color-text-subtle)' }}
                              axisLine={{ stroke: 'var(--color-border)' }}
                              tickLine={false}
                            />
                            <YAxis
                              domain={[1, 5]}
                              tick={{ fontSize: 11, fill: 'var(--color-text-subtle)' }}
                              axisLine={false}
                              tickLine={false}
                              width={28}
                            />
                            <Tooltip content={<ChartTooltip />} />
                            <Line
                              type="monotone"
                              dataKey="average"
                              name="Média"
                              stroke={BRAND_BLUE}
                              strokeWidth={2}
                              dot={false}
                              activeDot={{ r: 4 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      )}

                      {csat.negative_ratings.length > 0 && (
                        <div className="csat-negative">
                          <h4>Avaliações negativas (1–2 estrelas)</h4>
                          <Table
                            columns={[
                              { key: 'hotel_name', label: 'Hotel' },
                              { key: 'rating', label: 'Rating' },
                              {
                                key: 'comment',
                                label: 'Comentário',
                                render: (r) => r.comment || '—',
                              },
                              {
                                key: 'created_at',
                                label: 'Data',
                                render: (r) =>
                                  r.created_at ? new Date(r.created_at).toLocaleDateString() : '—',
                              },
                            ]}
                            data={csat.negative_ratings}
                          />
                        </div>
                      )}
                    </Card>
                  ),
                ofertas: () =>
                  offers?.available && (
                    <Card title="Ofertas Especiais">
                      <div className="stat-grid">
                        <StatCard label="Acessos" value={offers.opened} icon={<MessageIcon />} />
                        <StatCard
                          label="Sessões únicas"
                          value={offers.unique_sessions}
                          icon={<Users />}
                        />
                        <StatCard
                          label="Visualizadores únicos"
                          value={offers.unique_viewers}
                          icon={<Eye />}
                        />
                        <StatCard
                          label="Cliques únicos"
                          value={offers.unique_clickers}
                          icon={<MousePointer />}
                        />
                        <StatCard
                          label="Cliques totais"
                          value={offers.clicked}
                          icon={<MousePointer />}
                        />
                        <StatCard
                          label="Cliques CTA / links externos"
                          value={offers.cta_clicks}
                          icon={<MousePointer />}
                        />
                        <StatCard
                          label="CTR Interação"
                          value={formatPercent(offers.ctr_interaction != null ? offers.ctr_interaction / 100 : null)}
                        />
                        <StatCard
                          label="CTR Conversão"
                          value={formatPercent(offers.ctr_conversion != null ? offers.ctr_conversion / 100 : null)}
                        />
                      </div>

                      {offers.by_offer.length === 0 ? (
                        <div className="empty">Sem dados suficientes.</div>
                      ) : (
                        <Table columns={offerColumns} data={offers.by_offer} />
                      )}
                    </Card>
                  ),
                horario: () => (
                  <Card title="Horário das conversas">
                    {data ? (
                      <>
                        <div className="stat-grid">
                          <StatCard
                            label="Hora de maior utilização"
                            value={formatPeakHour(data.heatmapPeaks?.peakHour)}
                            icon={<Clock />}
                          />
                          <StatCard
                            label="Dia da semana mais ativo"
                            value={data.heatmapPeaks?.peakDayLabel || '—'}
                            icon={<Align />}
                          />
                        </div>
                        <Heatmap grid={data.hourHeatmap} />
                      </>
                    ) : (
                      <div className="empty">Sem dados suficientes.</div>
                    )}
                  </Card>
                ),
                handoff: () =>
                  handoff?.available && (
                    <Card title="Atendimento Humano">
                      <div className="stat-grid">
                        <StatCard
                          label="Conversas para operador"
                          value={handoff.total}
                          hint={
                            handoff.total && data?.kpis.conversasPeriodo
                              ? `${formatPercent(handoff.total / data.kpis.conversasPeriodo)} das conversas no período`
                              : undefined
                          }
                          icon={<Headphones />}
                        />
                        <StatCard
                          label="Avaliação média"
                          value={handoff.avg_rating != null ? `${handoff.avg_rating} / 5` : '—'}
                          hint={handoff.rated_count ? `${handoff.rated_count} respostas` : undefined}
                          icon={<CheckCircle />}
                        />
                        <StatCard
                          label="Tempo médio de espera"
                          value={formatSeconds(handoff.avg_wait_seconds)}
                          icon={<Clock />}
                        />
                        <StatCard
                          label="Tempo médio de atendimento"
                          value={formatSeconds(handoff.avg_handle_seconds)}
                          icon={<Clock />}
                        />
                        <StatCard
                          label="Horário de pico de pedidos"
                          value={formatPeakHour(handoff.peak_hour)}
                          icon={<Align />}
                        />
                      </div>

                      {handoff.close_reasons.length > 0 && (
                        <>
                          <h4>Motivos de encerramento</h4>
                          <Table columns={handoffReasonColumns} data={handoff.close_reasons} />
                        </>
                      )}

                      {handoff.hotel_ranking.length > 1 && (
                        <>
                          <h4>Hotel com mais encaminhamentos</h4>
                          <Table columns={handoffHotelColumns} data={handoff.hotel_ranking} />
                        </>
                      )}

                      {handoff.pending_contacts.length > 0 && (
                        <>
                          <h4>Contactos para retorno de chamada</h4>
                          <p className="chart-note">
                            Hóspedes que deixaram contacto por não haver operador disponível na
                            altura — ainda não têm ecrã próprio, ficam aqui até serem contactados.
                          </p>
                          <Table columns={pendingContactColumns} data={handoff.pending_contacts} />
                        </>
                      )}
                    </Card>
                  ),
              };

              const orderedCards = cardOrder
                .map((id) => ({ id, node: cardRenderers[id]?.() }))
                .filter((c) => c.node);

              return (
                <DndContext
                  sensors={dragSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleCardDragEnd}
                >
                  <SortableContext
                    items={orderedCards.map((c) => c.id)}
                    strategy={rectSwappingStrategy}
                  >
                    <div className="chart-grid">
                      {orderedCards.map(({ id, node }) => (
                        <SortableCard key={id} id={id}>
                          {node}
                        </SortableCard>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
}
